<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Bancos</h1>
    </div>

    <div class="table-container">
      <div class="table-header">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-start position-relative">

          <div class="w-full w-md-auto md-flex-1">
            <div class="d-flex justify-content-between align-items-center justify-content-lg-start">
              <button
                type="button"
                class="filter d-flex align-items-center justify-content-between border-0"
                @click="showFilters = !showFilters"
              >
                <i class="ri-filter-3-line text-dark font-weight-bold"></i>
                <div>Filtrar</div>
              </button>
              <div class="d-md-none">
                <button type="button" class="button-tooltip text-dark font-weight-bold m-0"
                  v-b-tooltip.hover title="Recargar" @click="loadData()">
                  <i class="ri-loop-right-line"></i>
                </button>
              </div>
            </div>
          </div>

          <section class="w-full w-md-auto md-flex-1 d-flex justify-content-end align-items-center mt-2 mt-md-0">
            <div class="d-none d-md-flex">
              <button type="button" class="button-tooltip text-dark font-weight-bold"
                v-b-tooltip.hover title="Recargar" @click="loadData()">
                <i class="ri-loop-right-line"></i>
              </button>
            </div>

            <div class="w-full w-md-auto d-flex flex-column flex-md-row">
              <SearchBar
                placeholder="Buscar por nombre"
                @search="onSearch"
                class="flex-1"
              />
              <div class="text-right mt-2 mt-md-0">
                <b-button
                  :to="{ name: 'bank-create' }"
                  variant="none"
                  class="button button-primary ml-md-2"
                >Agregar</b-button>
              </div>
            </div>
          </section>

          <filter-menu
            :show="showFilters"
            @hide="showFilters = false"
            :options="filterOptions"
            :selectedOptionFilter="filters"
            @setFilters="setFilters"
          ></filter-menu>
        </div>

        <div class="w-100 d-flex flex-wrap align-items-center mt-2" v-if="showFiltersList">
          <span v-for="(val, name) in filterList" v-bind:key="name" class="filter-item">
            {{ getFilterName(name, val) }}
            <button type="button" class="text-white p-0 bg-transparent border-0 ml-2" @click="deleteFilter(name)">
              <i class="ri-close-circle-line"></i>
            </button>
          </span>
        </div>
      </div>

      <div>
        <!-- Mobile -->
        <div class="custom-table-mobile d-xl-none">
          <section class="custom-table-mobile-header">
            <div><span>Nombre</span></div>
            <div><span>Estado</span></div>
          </section>
          <div class="custom-table-mobile-body">
            <div class="custom-table-mobile-row" v-for="o in filteredList" :key="o.id">
              <div class="custom-table-mobile-grid">
                <div><span>{{ o.name }}</span></div>
                <div>
                  <span :class="`badge ${getStatus(o.status).color}`">{{ getStatus(o.status).label }}</span>
                </div>
                <div>
                  <button type="button" class="border-0 bg-transparent"
                    @click="o.showMobileData = !o.showMobileData">
                    <i :class="o.showMobileData ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                  </button>
                </div>
              </div>
              <div v-if="o.showMobileData" class="pt-3">
                <ul class="list-unstyled p-0">
                  <li><span class="font-weight-medium mr-2">Código:</span>{{ o.code }}</li>
                </ul>
              </div>
              <div class="custom-table-mobile-options mt-3">
                <router-link :to="{ name: 'bank-edit', params: { id: o.id } }" class="mx-3">Editar</router-link>
                <a href="" @click.prevent="openChangeStatus(o)">{{ getStatus(o.status).action }}</a>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop -->
        <table class="custom-table w-100 d-none d-xl-table">
          <thead>
            <tr>
              <th>CÓDIGO</th>
              <th>NOMBRE</th>
              <th>ESTADO</th>
              <th class="pr-md-3">OPCIONES</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="o in filteredList" :key="o.id">
              <td class="pl-3">{{ o.code }}</td>
              <td class="pl-3">{{ o.name }}</td>
              <td class="cell-center pl-md-2">
                <span :class="`badge ${getStatus(o.status).color}`">{{ getStatus(o.status).label }}</span>
              </td>
              <td class="cell-center d_option pr-md-2 position-relative">
                <DropdownTable>
                  <template slot="dropdown-content">
                    <DropdownItem
                      text="Editar"
                      :to="{ name: 'bank-edit', params: { id: o.id } }"
                    />
                    <DropdownItem
                      :text="getStatus(o.status).action"
                      @click="openChangeStatus(o)"
                    />
                  </template>
                </DropdownTable>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="table-footer d-flex justify-content-end align-items-center">
        <div class="table-no-results" v-if="filteredList.length === 0">
          <p class="m-0 px-3">No se encontraron resultados.</p>
        </div>
        <p v-else class="m-0 px-3 text-muted small">{{ filteredList.length }} registro(s)</p>
      </div>
    </div>

    <ConfirmationModal
      :show="confirmationModal.show"
      @hide="closeConfirmationModal"
      :data="confirmationModal"
      @confirm="confirmModal"
    />
  </div>
</template>

<script>
import {
  Constants,
  Util,
  Alert,
  FilterMenu,
  FilterMixin,
  DropdownTable,
  DropdownItem,
  SearchBar,
  ConfirmationModal,
  ConfirmationModalMixin,
} from "wize-admin";

import { Constants as LocalConstants } from "@/core/utils";
import { BankService } from "@/core/services";

export default {
  mixins: [ConfirmationModalMixin, FilterMixin],
  components: { FilterMenu, DropdownTable, DropdownItem, SearchBar, ConfirmationModal },

  data() {
    return {
      list:        [],
      search:      "",
      showFilters: false,
      filterOptions: [
        {
          label: "Estado",
          key: "status",
          type: Constants.TypeFilters.SINGLE,
          options: [
            LocalConstants.BankStatus.ACTIVE,
            LocalConstants.BankStatus.INACTIVE,
          ],
        },
      ],
    };
  },

  computed: {
    filteredList() {
      if (!this.search) return this.list;
      const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const q = norm(this.search);
      return this.list.filter((o) =>
        norm(o.name).includes(q) ||
        norm(o.code).includes(q)
      );
    },
  },

  methods: {
    async loadData() {
      try {
        await this.$store.dispatch("app/loading", true);
        const params = {};
        if (this.filters.status != null) params.status = this.filters.status;
        const resp = await BankService.list(params);
        this.list = (resp.payload || []).map((item) => ({
          ...item,
          showMobileData: false,
        }));
      } catch (e) {
        console.error(e);
        Alert.error(e.errors ? e.errors.message : "Error al cargar los bancos");
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },

    onSearch(val) {
      this.search = val;
    },

    getStatus(key) {
      return Util.searchValue(parseInt(key), LocalConstants.BankStatus);
    },

    openChangeStatus(o) {
      this.selectedOption = o;
      this.openConfirmationModal({
        title:       "Cambiar estado",
        description: "¿Está seguro que desea cambiar el estado de este banco?",
        acceptText:  "Aceptar",
        action:      "update",
      });
    },

    confirmModal() {
      this.closeConfirmationModal();
      if (this.confirmationModal.action === "update") this.changeStatus();
    },

    async changeStatus() {
      try {
        await this.$store.dispatch("app/loading", true);
        const current   = parseInt(this.selectedOption.status);
        const newStatus =
          current === LocalConstants.BankStatus.ACTIVE.key
            ? LocalConstants.BankStatus.INACTIVE.key
            : LocalConstants.BankStatus.ACTIVE.key;
        await BankService.changeStatus(this.selectedOption.id, newStatus);
        await this.loadData();
        Alert.success("Estado actualizado correctamente");
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "Error al cambiar el estado");
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
  },

  async created() {
    await this.loadData();
  },
};
</script>

<style lang="stylus">
@import '../../Areas/Styles/supplier-list';
</style>
